import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useHub } from "../hooks/useHub";
import { clearToken, updateNickname } from "../lib/api";
import LogoTile from "../components/LogoTile";
import TabBar from "../components/TabBar";
import { useT } from "../i18n";
import type { Lang as LangCode } from "../i18n";
import { errorMessage } from "../i18n/errors";

const LANG_OPTIONS: { code: LangCode; label: string }[] = [
  { code: "kk", label: "ҚА" },
  { code: "ru", label: "РУ" },
  { code: "en", label: "EN" },
];

export default function Profile() {
  const nav = useNavigate();
  const { t, lang, setLang } = useT();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";
  const q = `?phone=${encodeURIComponent(phone)}`;

  const { hub, reload } = useHub(phone || null);

  // Смена ника: поле заполняем текущим ником один раз, когда хаб загрузился.
  const [nick, setNick] = useState("");
  const nickInited = useRef(false);
  const [nickBusy, setNickBusy] = useState(false);
  const [nickMsg, setNickMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    if (hub && !nickInited.current) {
      nickInited.current = true;
      setNick(hub.participant.nickname || "");
    }
  }, [hub]);

  async function saveNick() {
    setNickMsg(null);
    const value = nick.trim();
    if (!value) return setNickMsg({ ok: false, text: t("register.errNoNick") });
    if (!/^[A-Za-z0-9_]{2,20}$/.test(value))
      return setNickMsg({ ok: false, text: t("register.errNickLatin") });
    setNickBusy(true);
    try {
      await updateNickname(value);
      setNickMsg({ ok: true, text: t("profile.nickSaved") });
      reload();
    } catch (e: unknown) {
      setNickMsg({ ok: false, text: errorMessage(e, t) });
    } finally {
      setNickBusy(false);
    }
  }

  if (!phone) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-6 text-muted text-sm">
        {t("hub.noPhone")}
      </div>
    );
  }

  const name = hub?.participant.nickname || t("hub.greetingFallback");
  const clan = hub?.clan ?? null;
  const prize = hub?.prize ?? null;

  const prizeStatus = prize?.granted
    ? t("profile.status.prizeGranted")
    : prize?.eligible
      ? t("profile.status.prizeEligible")
      : t("profile.status.prizeLocked");

  return (
    <div className="min-h-dvh max-w-md mx-auto px-[22px] pt-4 pb-28">
      <header className="flex items-center gap-3 pt-1.5 mb-5">
        <LogoTile size={46} radius={15} fontSize={16} />
        <div className="flex-1 min-w-0">
          <h1 className="text-[21px] font-extrabold tracking-tight leading-[1.1]">{name}</h1>
          <p className="text-[13px] text-muted mt-[3px]">{t("profile.title")}</p>
        </div>
      </header>

      <div className="flex flex-col gap-3.5">
        {/* Phone */}
        <section className="bg-card border border-line-2 rounded-[18px] px-4 py-3.5">
          <div className="text-[13px] text-muted">{t("profile.phoneLabel")}</div>
          <div className="font-display text-[18px] font-semibold text-white mt-0.5">{phone}</div>
        </section>

        {/* Nickname */}
        <section className="bg-card border border-line-2 rounded-[18px] px-4 py-3.5">
          <div className="text-[13px] font-bold text-white mb-2.5">{t("profile.nickLabel")}</div>
          <div className="flex gap-2">
            <input
              value={nick}
              // Те же правила, что при регистрации: латиница/цифры/_, без пробелов.
              onChange={(e) => {
                setNick(e.target.value.replace(/[^A-Za-z0-9_]/g, "").slice(0, 20));
                setNickMsg(null);
              }}
              placeholder={t("register.nickPlaceholder")}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="flex-1 min-w-0 bg-input text-black rounded-[12px] px-3.5 py-2.5 text-sm placeholder:text-grey/70 outline-none focus:ring-2 focus:ring-violet"
            />
            <button
              type="button"
              onClick={saveNick}
              disabled={nickBusy || !nick.trim() || nick.trim() === (hub?.participant.nickname ?? "")}
              className="h-[42px] px-4 rounded-[12px] bg-violet-grad text-white font-bold text-sm active:opacity-90 disabled:opacity-50 transition-opacity shadow-violet-btn shrink-0"
            >
              {nickBusy ? t("profile.nickSaving") : t("profile.nickSave")}
            </button>
          </div>
          {nickMsg && (
            <p className={`text-sm mt-2 ${nickMsg.ok ? "text-success" : "text-red-400"}`}>{nickMsg.text}</p>
          )}
        </section>

        {/* Status */}
        <section className="bg-card border border-line-2 rounded-[18px] px-4 py-3.5">
          <div className="text-[13px] font-bold text-white mb-2.5">{t("profile.statusTitle")}</div>
          <ul className="flex flex-col gap-2 text-sm text-white/85">
            <li className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${clan ? "bg-success" : "bg-faint"}`} />
              {clan ? t("profile.status.clanIn", { name: clan.name }) : t("profile.status.clanNone")}
            </li>
            <li className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${prize?.granted ? "bg-success" : prize?.eligible ? "bg-gold" : "bg-faint"}`} />
              {prizeStatus}
            </li>
          </ul>
        </section>

        {/* Language switcher */}
        <section className="bg-card border border-line-2 rounded-[18px] px-4 py-3.5">
          <div className="text-[13px] font-bold text-white mb-2.5">{t("profile.langLabel")}</div>
          <div className="flex gap-2">
            {LANG_OPTIONS.map((opt) => (
              <button
                key={opt.code}
                type="button"
                onClick={() => setLang(opt.code)}
                className={`flex-1 h-11 rounded-[14px] font-bold text-sm transition-colors ${
                  lang === opt.code
                    ? "bg-violet-grad text-white shadow-violet"
                    : "bg-white/[0.06] text-white/80 active:bg-white/10"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        <button
          type="button"
          onClick={() => nav(`/hub${q}`)}
          className="w-full h-[50px] rounded-[14px] bg-violet-grad text-white font-extrabold text-[15px] active:opacity-90 transition-opacity shadow-violet-btn"
        >
          {t("profile.toHub")}
        </button>

        <button
          type="button"
          onClick={() => {
            clearToken();
            nav("/login");
          }}
          className="w-full h-12 rounded-[14px] bg-white/[0.06] text-white/80 font-medium text-sm ring-1 ring-white/10 active:bg-white/10 transition-colors"
        >
          {t("profile.logout")}
        </button>
      </div>

      <TabBar active="profile" phone={phone} />
    </div>
  );
}
